import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import ShellThemeApplier from '../common/ShellThemeApplier';
import Footer from '../common/Footer';
import UserMenu from './UserMenu';
import SidebarNav from './SidebarNav';
import { formatBadgeCount } from '../../../utils/numberUtils';
import {
  DEFAULT_SIDEBAR_MODE,
  getAccountSidebarMode,
  applySidebarMode,
  cycleSidebarMode,
  sidebarModeButtonTitle,
  isValidSidebarMode,
} from '../../utils/sidebarMode';
import { UI_PREFERENCES_EVENT } from '../../utils/uiPreferences';
import './AppShell.css';

const PHONE_MQ = '(max-width: 640px)';
/** Delay single-click flyout so double-click can exit hamburger without flicker. */
const HAMBURGER_CLICK_DELAY_MS = 280;

function useIsPhone() {
  const [isPhone, setIsPhone] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(PHONE_MQ).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(PHONE_MQ);
    const onChange = () => setIsPhone(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isPhone;
}

/**
 * Permanent app chrome: left sidebar + slim top bar + main.
 */
export default function AppShell({
  role = 'guest',
  logo,
  navGroups = [],
  children,
  user,
  currentView,
  alertCount = 0,
  showAlerts = true,
  onAlertsClick,
  onProfileClick,
  onPreferencesClick,
  onHelpClick,
  onLogout,
  mobileMenuOpen,
  setMobileMenuOpen,
}) {
  const navRef = useRef(null);
  const toggleRef = useRef(null);
  const hamburgerClickTimerRef = useRef(null);
  const isPhone = useIsPhone();
  // Always start icons-only; adopt account default after ShellThemeApplier writes session
  const [sidebarMode, setSidebarMode] = useState(DEFAULT_SIDEBAR_MODE);

  useEffect(() => {
    applySidebarMode(sidebarMode);
  }, [sidebarMode]);

  // useLayoutEffect: catch ShellThemeApplier prefs in the same commit (child layout runs first)
  useLayoutEffect(() => {
    const adoptAccountDefault = () => {
      const next = getAccountSidebarMode() || DEFAULT_SIDEBAR_MODE;
      setSidebarMode(next);
      applySidebarMode(next);
    };
    const sync = (e) => {
      // Only adopt the saved default when Preferences (or login) changes sidebar_mode.
      // Backdrop / other prefs must not reset the live ☰ session density.
      if (e?.detail && e.detail.sidebar_mode !== true) return;
      adoptAccountDefault();
    };
    window.addEventListener(UI_PREFERENCES_EVENT, sync);
    adoptAccountDefault();
    return () => window.removeEventListener(UI_PREFERENCES_EVENT, sync);
  }, []);

  useEffect(() => () => {
    if (hamburgerClickTimerRef.current) {
      clearTimeout(hamburgerClickTimerRef.current);
      hamburgerClickTimerRef.current = null;
    }
  }, []);

  // ☰ cycle / double-click: session UI only — do not write Preferences / account default
  const applySessionMode = useCallback((mode) => {
    if (!isValidSidebarMode(mode)) return;
    setSidebarMode(mode);
    applySidebarMode(mode);
  }, []);

  const clearHamburgerClickTimer = () => {
    if (hamburgerClickTimerRef.current) {
      clearTimeout(hamburgerClickTimerRef.current);
      hamburgerClickTimerRef.current = null;
    }
  };

  const handleToggleClick = () => {
    // Phone or desktop hamburger: open temporary flyout only
    if (isPhone || sidebarMode === 'collapsed') {
      // When preference is hamburger, delay so double-click can exit without toggling twice
      if (sidebarMode === 'collapsed') {
        if (hamburgerClickTimerRef.current) {
          clearHamburgerClickTimer();
          return;
        }
        hamburgerClickTimerRef.current = setTimeout(() => {
          hamburgerClickTimerRef.current = null;
          setMobileMenuOpen((open) => !open);
        }, HAMBURGER_CLICK_DELAY_MS);
        return;
      }
      setMobileMenuOpen((open) => !open);
      return;
    }
    // Expanded / icons: cycle expanded → icon → collapsed (Preferences default unchanged)
    const next = cycleSidebarMode(sidebarMode);
    applySessionMode(next);
    setMobileMenuOpen(false);
  };

  const handleToggleDoubleClick = () => {
    if (sidebarMode !== 'collapsed') return;
    clearHamburgerClickTimer();
    // Hamburger → full sidebar (expanded); single-click only toggles the flyout
    applySessionMode('expanded');
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handlePointerDown = (e) => {
      if (toggleRef.current?.contains(e.target)) return;
      if (navRef.current?.contains(e.target)) return;
      setMobileMenuOpen(false);
    };

    const handleKey = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [mobileMenuOpen, setMobileMenuOpen]);

  const collapsed = sidebarMode === 'collapsed' && !isPhone;
  const showHeaderToggle = collapsed || isPhone;
  const flyoutOpen = Boolean(mobileMenuOpen);

  const hamburgerHint =
    sidebarMode === 'collapsed'
      ? (flyoutOpen
        ? 'Close navigation menu · double-click for full sidebar'
        : 'Open navigation menu · double-click for full sidebar')
      : null;

  const toggleButton = (
    <button
      ref={toggleRef}
      type="button"
      className={`sidebar-mode-toggle${flyoutOpen ? ' active' : ''}`}
      onClick={handleToggleClick}
      onDoubleClick={handleToggleDoubleClick}
      title={
        hamburgerHint
        || (isPhone
          ? (flyoutOpen ? 'Close navigation menu' : 'Open navigation menu')
          : sidebarModeButtonTitle(sidebarMode))
      }
      aria-label={
        hamburgerHint
        || (isPhone
          ? (flyoutOpen ? 'Close navigation menu' : 'Open navigation menu')
          : sidebarModeButtonTitle(sidebarMode))
      }
      aria-expanded={isPhone || sidebarMode === 'collapsed' ? flyoutOpen : true}
    >
      ☰
    </button>
  );

  return (
    <div className="App" data-role={role} data-sidebar-mode={sidebarMode}>
      <ShellThemeApplier />

      <SidebarNav
        groups={navGroups}
        navRef={navRef}
        mobileOpen={flyoutOpen}
        onNavigate={() => setMobileMenuOpen(false)}
        densityToggle={!showHeaderToggle ? toggleButton : null}
      />

      <div className="app-frame">
        <header className={`app-header app-header--shell${showHeaderToggle ? ' app-header--with-toggle' : ''}`}>
          {showHeaderToggle ? toggleButton : null}

          <div className="header-logo">{logo}</div>

          <div className="header-right-controls">
            {onHelpClick && (
              <button
                type="button"
                className={`header-help-btn${currentView === 'help' ? ' active' : ''}`}
                onClick={onHelpClick}
                title="Help guides"
                aria-label="Help guides"
              >
                <span className="header-help-face" aria-hidden="true">
                  <svg
                    className="header-help-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.85"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 7v14" />
                    <path d="M5 5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2V5z" />
                  </svg>
                </span>
              </button>
            )}
            {showAlerts && (
              <button
                type="button"
                className={`header-alerts-btn${currentView === 'alerts' ? ' active' : ''}`}
                onClick={onAlertsClick}
                title={alertCount > 0 ? `Alerts (${alertCount})` : 'Alerts'}
                aria-label={alertCount > 0 ? `Alerts (${alertCount})` : 'Alerts'}
              >
                <span className="header-alerts-face" aria-hidden="true">
                  <span className="header-alerts-icon">🔔</span>
                </span>
                {alertCount > 0 && (
                  <span className="header-alerts-badge">{formatBadgeCount(alertCount)}</span>
                )}
              </button>
            )}
            <UserMenu
              user={user}
              organizationName={null}
              onProfileClick={onProfileClick}
              onPreferencesClick={onPreferencesClick}
              onLogout={onLogout}
            />
          </div>
        </header>

        <main className="app-main">{children}</main>
        <Footer />
      </div>
    </div>
  );
}
